import { cellKey, cellsAdjacent, isObstacle, nodeOccupiesCell, nodePortCell } from './grid.js';
import { edgeKeyFromCells } from './network.js';
import type { Cable, CellCoord, GameNode, GameState } from './types.js';

export interface CableBuildResult {
  ok: boolean;
  error?: string;
  newCellsCost?: number;
  cable?: Cable;
}

function allCableCellKeys(state: GameState, excludeCableId?: string): Set<string> {
  const set = new Set<string>();
  for (const cable of state.cables) {
    if (cable.id === excludeCableId) continue;
    for (const c of cable.cells) set.add(cellKey(c));
  }
  return set;
}

function nodeAtCell(nodes: GameNode[], c: CellCoord): GameNode | undefined {
  return nodes.find((n) => nodeOccupiesCell(n, c));
}

function nodeAtPort(nodes: GameNode[], c: CellCoord): GameNode | undefined {
  return nodes.find((n) => {
    const p = nodePortCell(n);
    return p.x === c.x && p.y === c.y;
  });
}

function canTraverseCell(
  state: GameState,
  c: CellCoord,
  endpointNodeIds: Set<string>,
  isEndpoint: boolean,
): boolean {
  if (c.x < 0 || c.y < 0 || c.x >= state.gridWidth || c.y >= state.gridHeight) return false;
  if (isObstacle(state.terrain, c)) return false;

  const occupant = nodeAtCell(state.nodes, c);
  if (!occupant) return true;

  // Relays / switches: cables may pass through (junction).
  if (occupant.kind === 'relay' || occupant.kind === 'switch') {
    return true;
  }
  // Generators / teleporters: 1×1 endpoints only (not mid-path).
  if (occupant.kind === 'generator' || occupant.kind === 'teleporter') {
    return isEndpoint;
  }
  // Bulbs: only the dedicated port cell, and only as an endpoint.
  const port = nodePortCell(occupant);
  const onPort = port.x === c.x && port.y === c.y;
  if (!onPort) return false;
  if (isEndpoint && endpointNodeIds.has(occupant.id)) return true;
  return false;
}

export function computeNewCellsForPath(
  path: CellCoord[],
  existingCableCells: Set<string>,
): number {
  let cost = 0;
  for (const c of path) {
    if (!existingCableCells.has(cellKey(c))) cost++;
  }
  return cost;
}

/** Resolve node id if a cell is exactly that node's port. */
export function resolvePortNodeId(state: GameState, c: CellCoord): string | null {
  const n = nodeAtPort(state.nodes, c);
  return n ? n.id : null;
}

/**
 * Free-form cable: may start/end in empty space, on a cable, or on a building port.
 * Does not require both ends to be buildings.
 */
export function validateCablePath(
  state: GameState,
  path: CellCoord[],
  startNodeId: string | null,
  endNodeId: string | null,
  _branchFromExisting = false,
  excludeCableId?: string | null,
): CableBuildResult {
  if (path.length < 2) {
    return { ok: false, error: 'path_too_short' };
  }

  for (let i = 0; i < path.length - 1; i++) {
    if (!cellsAdjacent(path[i]!, path[i + 1]!)) {
      return { ok: false, error: 'not_adjacent' };
    }
  }

  // Forbid stacking same-direction segments (shared edges). Crossings OK.
  const existingEdges = new Set<string>();
  for (const cable of state.cables) {
    if (excludeCableId && cable.id === excludeCableId) continue;
    for (let i = 0; i < cable.cells.length - 1; i++) {
      existingEdges.add(edgeKeyFromCells(cable.cells[i]!, cable.cells[i + 1]!));
    }
  }
  for (let i = 0; i < path.length - 1; i++) {
    const ek = edgeKeyFromCells(path[i]!, path[i + 1]!);
    if (existingEdges.has(ek)) {
      return { ok: false, error: 'edge_overlap' };
    }
  }

  const startCell = path[0]!;
  const endCell = path[path.length - 1]!;

  const resolvedStart = startNodeId ?? resolvePortNodeId(state, startCell);
  const resolvedEnd = endNodeId ?? resolvePortNodeId(state, endCell);

  if (resolvedStart && resolvedEnd && resolvedStart === resolvedEnd && path.length < 3) {
    return { ok: false, error: 'same_node' };
  }

  // If claimed to start/end on a node, must be on that node's port.
  if (resolvedStart) {
    const n = state.nodes.find((x) => x.id === resolvedStart);
    if (!n) return { ok: false, error: 'invalid_nodes' };
    const p = nodePortCell(n);
    if (p.x !== startCell.x || p.y !== startCell.y) {
      return { ok: false, error: 'start_not_on_port' };
    }
  } else {
    // Free start: must not sit on a non-port building cell.
    const occ = nodeAtCell(state.nodes, startCell);
    if (occ && occ.kind !== 'relay' && occ.kind !== 'switch') {
      const p = nodePortCell(occ);
      if (p.x !== startCell.x || p.y !== startCell.y) {
        return { ok: false, error: 'start_blocked' };
      }
    }
  }

  if (resolvedEnd) {
    const n = state.nodes.find((x) => x.id === resolvedEnd);
    if (!n) return { ok: false, error: 'invalid_nodes' };
    const p = nodePortCell(n);
    if (p.x !== endCell.x || p.y !== endCell.y) {
      return { ok: false, error: 'end_not_on_port' };
    }
  } else {
    const occ = nodeAtCell(state.nodes, endCell);
    if (occ && occ.kind !== 'relay' && occ.kind !== 'switch') {
      const p = nodePortCell(occ);
      if (p.x !== endCell.x || p.y !== endCell.y) {
        return { ok: false, error: 'end_blocked' };
      }
    }
  }

  const endpoints = new Set<string>();
  if (resolvedStart) endpoints.add(resolvedStart);
  if (resolvedEnd) endpoints.add(resolvedEnd);

  for (let i = 0; i < path.length; i++) {
    const c = path[i]!;
    const isEnd = i === 0 || i === path.length - 1;
    if (!canTraverseCell(state, c, endpoints, isEnd)) {
      return { ok: false, error: isEnd ? 'blocked_end' : 'blocked_mid' };
    }
  }

  const existing = allCableCellKeys(state, excludeCableId ?? undefined);
  const newCost = computeNewCellsForPath(path, existing);

  if (newCost > state.cablesRemaining) {
    return { ok: false, error: 'insufficient_cables' };
  }

  return {
    ok: true,
    newCellsCost: newCost,
    cable: {
      id: '',
      cells: path.map((p) => ({ ...p })),
      endpointA: resolvedStart,
      endpointB: resolvedEnd,
    },
  };
}

export function reclaimCellsFromCable(
  cable: Cable,
  remainingCables: Cable[],
): number {
  const stillUsed = new Set<string>();
  for (const c of remainingCables) {
    for (const cell of c.cells) stillUsed.add(cellKey(cell));
  }
  let reclaimed = 0;
  for (const cell of cable.cells) {
    if (!stillUsed.has(cellKey(cell))) reclaimed++;
  }
  return reclaimed;
}

export function eraseCableFromState(state: GameState, cableId: string): boolean {
  const idx = state.cables.findIndex((c) => c.id === cableId);
  if (idx < 0) return false;
  const removed = state.cables[idx]!;
  const remaining = state.cables.filter((c) => c.id !== cableId);
  state.cablesRemaining += reclaimCellsFromCable(removed, remaining);
  state.cables.splice(idx, 1);
  state.pulses = state.pulses.filter((p) => p.cableId !== cableId && !p.routeCableIds.includes(cableId));
  state.networkVersion++;
  return true;
}

/**
 * Erase a single cell from a cable. Splits the cable into two segments when
 * removing a middle cell; stubs shorter than 2 cells are discarded and refunded.
 */
export function eraseCableCellFromState(
  state: GameState,
  cableId: string,
  cellIndex: number,
): boolean {
  const idx = state.cables.findIndex((c) => c.id === cableId);
  if (idx < 0) return false;
  const cable = state.cables[idx]!;
  if (cellIndex < 0 || cellIndex >= cable.cells.length) return false;

  const left = cable.cells.slice(0, cellIndex).map((c) => ({ ...c }));
  const right = cable.cells.slice(cellIndex + 1).map((c) => ({ ...c }));
  const removed = { ...cable.cells[cellIndex]! };

  state.cables.splice(idx, 1);
  state.pulses = state.pulses.filter(
    (p) => p.cableId !== cableId && !p.routeCableIds.includes(cableId),
  );

  const usedKeys = (): Set<string> => {
    const s = new Set<string>();
    for (const c of state.cables) {
      for (const cell of c.cells) s.add(cellKey(cell));
    }
    return s;
  };

  const refundFree = (cells: CellCoord[]) => {
    const used = usedKeys();
    for (const c of cells) {
      if (!used.has(cellKey(c))) state.cablesRemaining++;
    }
  };

  refundFree([removed]);

  const pushSeg = (cells: CellCoord[]) => {
    if (cells.length < 2) {
      refundFree(cells);
      return;
    }
    const id = `c${state.nextCableId++}`;
    state.cables.push({
      id,
      cells,
      endpointA: resolvePortNodeId(state, cells[0]!),
      endpointB: resolvePortNodeId(state, cells[cells.length - 1]!),
    });
  };

  pushSeg(left);
  pushSeg(right);
  state.networkVersion++;
  return true;
}

